import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  ReactNode,
} from "react";
import { Product, SortOption } from "../types/product";
import * as api from "../services/api";

interface ProductState {
  products: Product[];
  selectedProduct: Product | null;
  categories: string[];
  loading: boolean;
  error: string | null;
  sortOption: SortOption;
  filterCategory: string;
  currentPage: number;
}

const initialState: ProductState = {
  products: [],
  selectedProduct: null,
  categories: [],
  loading: false,
  error: null,
  sortOption: "price-asc",
  filterCategory: "",
  currentPage: 1,
};

type ProductAction =
  | { type: "SET_LOADING"; payload: boolean }
  | { type: "SET_ERROR"; payload: string | null }
  | { type: "SET_PRODUCTS"; payload: Product[] }
  | { type: "SET_SELECTED_PRODUCT"; payload: Product | null }
  | { type: "SET_CATEGORIES"; payload: string[] }
  | { type: "SET_SORT_OPTION"; payload: SortOption }
  | { type: "SET_FILTER_CATEGORY"; payload: string }
  | { type: "SET_CURRENT_PAGE"; payload: number };

function productReducer(
  state: ProductState,
  action: ProductAction
): ProductState {
  switch (action.type) {
    case "SET_LOADING":
      return { ...state, loading: action.payload };
    case "SET_ERROR":
      return { ...state, error: action.payload };
    case "SET_PRODUCTS":
      return { ...state, products: action.payload };
    case "SET_SELECTED_PRODUCT":
      return { ...state, selectedProduct: action.payload };
    case "SET_CATEGORIES":
      return { ...state, categories: action.payload };
    case "SET_SORT_OPTION":
      // reset to page 1 when sorting changes
      return { ...state, sortOption: action.payload, currentPage: 1 };
    case "SET_FILTER_CATEGORY":
      return { ...state, filterCategory: action.payload, currentPage: 1 };
    case "SET_CURRENT_PAGE":
      return { ...state, currentPage: action.payload };
    default:
      return state;
  }
}

interface ProductContextType {
  state: ProductState;
  loadProducts: () => Promise<void>;
  loadProductById: (id: number) => Promise<void>;
  loadCategories: () => Promise<void>;
  setSortOption: (option: SortOption) => void;
  setFilterCategory: (category: string) => void;
  setCurrentPage: (page: number) => void;
  clearSelectedProduct: () => void;
  clearError: () => void;
}

const ProductContext = createContext<ProductContextType | undefined>(undefined);

export const ProductProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(productReducer, initialState);

  const loadProducts = useCallback(async () => {
    dispatch({ type: "SET_LOADING", payload: true });
    dispatch({ type: "SET_ERROR", payload: null });
    try {
      const products = await api.fetchProducts();
      dispatch({ type: "SET_PRODUCTS", payload: products });
    } catch (err) {
      dispatch({
        type: "SET_ERROR",
        payload: "Failed to load products. Please try again later.",
      });
    } finally {
      dispatch({ type: "SET_LOADING", payload: false });
    }
  }, []);

  const loadProductById = useCallback(async (id: number) => {
    dispatch({ type: "SET_LOADING", payload: true });
    dispatch({ type: "SET_ERROR", payload: null });
    try {
      const product = await api.fetchProductById(id);
      dispatch({ type: "SET_SELECTED_PRODUCT", payload: product });
    } catch (err) {
      dispatch({
        type: "SET_ERROR",
        payload: "Failed to load product details. Please try again later.",
      });
    } finally {
      dispatch({ type: "SET_LOADING", payload: false });
    }
  }, []);

  const loadCategories = useCallback(async () => {
    try {
      const categories = await api.fetchCategories();
      dispatch({ type: "SET_CATEGORIES", payload: categories });
    } catch {
      // categories failing isn't critical, just silently skip
    }
  }, []);

  const setSortOption = useCallback((option: SortOption) => {
    dispatch({ type: "SET_SORT_OPTION", payload: option });
  }, []);

  const setFilterCategory = useCallback((category: string) => {
    dispatch({ type: "SET_FILTER_CATEGORY", payload: category });
  }, []);

  const setCurrentPage = useCallback((page: number) => {
    dispatch({ type: "SET_CURRENT_PAGE", payload: page });
  }, []);

  const clearSelectedProduct = useCallback(() => {
    dispatch({ type: "SET_SELECTED_PRODUCT", payload: null });
  }, []);

  const clearError = useCallback(() => {
    dispatch({ type: "SET_ERROR", payload: null });
  }, []);

  return (
    <ProductContext.Provider
      value={{
        state,
        loadProducts,
        loadProductById,
        loadCategories,
        setSortOption,
        setFilterCategory,
        setCurrentPage,
        clearSelectedProduct,
        clearError,
      }}
    >
      {children}
    </ProductContext.Provider>
  );
};

export function useProducts(): ProductContextType {
  const context = useContext(ProductContext);
  if (!context) {
    throw new Error("useProducts must be used within a ProductProvider");
  }
  return context;
}
